import DOMPurify from 'dompurify';

// Sanitize HTML content
export function sanitize(dirty: string): string {
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [] });
}

// Escape HTML for safe insertion
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Format time in seconds to MM:SS
export function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Debounce function
export function debounce<T extends (...args: Parameters<T>) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

// Throttle function
export function throttle<T extends (...args: Parameters<T>) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

// Generate deterministic color from string
export function getArtistColor(name: string): [string, string] {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colors: [string, string][] = [
    ['#ED5564', '#D63C4D'], // Red (primary)
    ['#FC6E51', '#E9573F'], // Orange
    ['#FFCE54', '#F6BB42'], // Yellow
    ['#A0D468', '#8CC152'], // Green
    ['#48CFAD', '#37BC9B'], // Mint
    ['#4FC1E9', '#3BAFDA'], // Blue
    ['#5D9CEC', '#4A89DC'], // Indigo
    ['#AC92EC', '#967ADC'], // Purple
    ['#EC87C0', '#D770AD'], // Pink
    ['#AAB2BD', '#656D78'], // Gray
  ];

  const idx = Math.abs(hash) % colors.length;
  return colors[idx] || ['#ED5564', '#D63C4D'];
}

// Get first letter of name for avatar
export function getInitial(name: string): string {
  return name.charAt(0).toUpperCase();
}

// Generate unique ID
export function generateId(): string {
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Copy to clipboard
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  } catch {
    return false;
  }
}

// Share content using Web Share API
export async function share(data: { title?: string; text?: string; url?: string }): Promise<boolean> {
  if (!navigator.share) {
    return false;
  }

  try {
    await navigator.share(data);
    return true;
  } catch (error) {
    // User cancelled or share failed
    return false;
  }
}

// Check if device is mobile
export function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

// Check if app is in standalone mode (PWA)
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

// Get theme preference
export function getThemePreference(): 'light' | 'dark' | 'auto' {
  const stored = localStorage.getItem('theme-preference');
  if (stored === 'light' || stored === 'dark' || stored === 'auto') {
    return stored;
  }
  return 'auto';
}

// Apply theme
export function applyTheme(theme: 'light' | 'dark' | 'auto'): void {
  const root = document.documentElement;

  let actualTheme: 'light' | 'dark' = 'dark';

  if (theme === 'auto') {
    actualTheme = window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } else {
    actualTheme = theme;
  }

  root.setAttribute('data-theme', actualTheme);
  localStorage.setItem('theme-preference', theme);
}

// Lazy load image
export function lazyLoadImage(img: HTMLImageElement): void {
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const image = entry.target as HTMLImageElement;
          const src = image.dataset.src;
          if (src) {
            image.src = src;
            image.removeAttribute('data-src');
          }
          observer.unobserve(image);
        }
      });
    });

    observer.observe(img);
  } else {
    // Fallback for browsers without IntersectionObserver
    const src = img.dataset.src;
    if (src) {
      img.src = src;
      img.removeAttribute('data-src');
    }
  }
}

// Request persistent storage
export async function requestPersistentStorage(): Promise<boolean> {
  if (navigator.storage && navigator.storage.persist) {
    const isPersisted = await navigator.storage.persist();
    return isPersisted;
  }
  return false;
}

// Show toast notification
let toastTimeout: ReturnType<typeof setTimeout> | null = null;

export function showToast(message: string, duration = 3000): void {
  let toast = document.getElementById('toast-notification');

  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'toast-notification';
    toast.className = 'toast';
    document.body.appendChild(toast);
  }

  toast.textContent = message;
  toast.classList.add('show');

  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }

  toastTimeout = setTimeout(() => {
    toast?.classList.remove('show');
  }, duration);
}

// Shuffle array (Fisher-Yates)
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  return shuffled;
}

// Smart shuffle (weighted random to prevent recent repeats)
export function smartShuffle<T>(array: T[], recentCount = 5): T[] {
  if (array.length <= recentCount) {
    return shuffleArray(array);
  }

  const recent = array.slice(0, recentCount);
  const remaining = array.slice(recentCount);
  const shuffledRemaining = shuffleArray(remaining);

  return [...recent, ...shuffledRemaining];
}
